<template>
    <client-only>
        <div
            :id="currentMapId"
            class="ui-map"
        />
    </client-only>
</template>

<script>
import {computed, defineComponent, nextTick, onMounted, shallowRef, watch} from 'vue';

import YandexMapBuilder from '@/core/utils/YandexMapBuilder';
import {settings} from '@/core/constants/ymapSettings';

const MAP_ID = `__ym-map-${Math.random()}__`;
const DEFAULT_MAP_OPTIONS = {
    center: [47.22913310868308, 39.71899174984997],
    zoom: 17,
    controls: ['zoomControl'],
};

export default defineComponent({
    name: 'UIMap',
    props: {
        mapId: {
            type: String,
            default: () => '',
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        markers: {
            type: Array,
            default: () => ([]),
        },
        withGeolocation: {
            type: Boolean,
            default: () => false,
        },
    },
    emits: ['map-was-initialized', 'click-on-cluster'],
    setup(props, context) {
        const mapBuilderInstance = shallowRef(null);

        const currentMapId = computed(() => {
            return props.mapId || MAP_ID;
        });

        const currentMapOptions = computed(() => {
            return {
                ...DEFAULT_MAP_OPTIONS,
                ...props.options,
            };
        });

        async function initMap() {
            mapBuilderInstance.value = new YandexMapBuilder({
                apiKey: settings.apiKey,
                mapId: currentMapId.value,
            });

            await mapBuilderInstance.value.buildMap({
                coordorder: settings.coordorder,
                version: settings.version,
                mapOptions: currentMapOptions.value,
            });

            mapBuilderInstance.value?.map && context.emit('map-was-initialized', mapBuilderInstance.value.map);

            if (props.markers && Array.isArray(props.markers) && props.markers.length) {
                setMarkers(props.markers);
            }

            if (props.withGeolocation) {
                mapBuilderInstance.value.setGeolocation({position: {top: 350, left: 10}, zoomAfterClick: 17});
            }
        }

        async function setMarkers(markers) {
            if (!mapBuilderInstance.value.map) {
                return;
            }

            mapBuilderInstance.value.map?.geoObjects?.removeAll();
            const geoObjects = [];
            markers.forEach(async({events, ...options}) => {
                const marker = await mapBuilderInstance.value.setMapMarker({options, events});
                geoObjects.push(marker);
            });

            if (!props.options?.clusterOptions) {
                return;
            }

            const cluster = await mapBuilderInstance.value.setClusters(props.options.clusterOptions, geoObjects);
            cluster.events.add('click', (event) => context.emit('click-on-cluster', event));
        }

        function updateMapOptions(options) {
            if (!(options.length || mapBuilderInstance.value?.map)) {
                return;
            }

            Object.keys(options).forEach((key) => {
                const setter = mapBuilderInstance.value.map[getSetterName(key)];
                typeof setter === 'function' && setter.call(mapBuilderInstance.value.map, options[key]);
            });
        }

        function getSetterName(name) {
            if (!name) {
                return;
            }

            return `set${name[0].toUpperCase()}${name.slice(1)}`;
        }

        onMounted(() => {
            nextTick().then(initMap);
        });

        watch(() => props.markers, setMarkers);
        watch(() => props.options, updateMapOptions);

        return {
            mapBuilderInstance,
            currentMapId,
        };
    },
});
</script>

<style lang="scss">
.ui-map {
    width: 100%;
    height: 100%;
}
</style>
